import { useEffect, useRef, useState } from 'react';
import { scroller } from 'react-scroll';
import styles from './herocta_v2.module.scss';

const SEGMENTS   = ['agentic engineer', ' | solutions architect'];
const LINE2      = '20+ years of craft';
const LINE3      = 'Starbucks · T-Mobile · Microsoft';

const CHAR_SPEED = 30;
const SEG_PAUSE  = 350;
const LINE_PAUSE = 380;
const CTA_PAUSE  = 300;

interface HeroCTA_v2Props {
  isAtTop: boolean;
  shouldStart: boolean;
  onCTAReady?: () => void;
}

const HeroCTA_v2: React.FC<HeroCTA_v2Props> = ({ isAtTop, shouldStart, onCTAReady }) => {
  const [line1, setLine1]           = useState('');
  const [line2, setLine2]           = useState('');
  const [line3, setLine3]           = useState('');
  const [line1Done, setLine1Done]   = useState(false);
  const [line2Done, setLine2Done]   = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [isVisible, setIsVisible]   = useState(false);
  const isAtTopRef                  = useRef(isAtTop);

  // Sync isAtTop ref and handle fade-out on scroll away
  useEffect(() => {
    isAtTopRef.current = isAtTop;
    if (!isAtTop) setIsVisible(false);
  }, [isAtTop]);

  // Show when at top
  useEffect(() => {
    if (isAtTopRef.current) setIsVisible(true);
  }, []);

  // Typewriter — starts when shouldStart becomes true (name complete)
  // Also fires on remount (scroll-to-top) if shouldStart is already true
  useEffect(() => {
    if (!shouldStart) return;

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const intervals: ReturnType<typeof setInterval>[] = [];

    const startDelay = setTimeout(() => {
      const seg0 = SEGMENTS[0];
      const seg1 = SEGMENTS[1];

      let i = 0;
      const t0 = setInterval(() => {
        i++;
        setLine1(seg0.slice(0, i));
        if (i >= seg0.length) {
          clearInterval(t0);

          const p0 = setTimeout(() => {
            let j = 0;
            const t1 = setInterval(() => {
              j++;
              setLine1(seg0 + seg1.slice(0, j));
              if (j >= seg1.length) {
                clearInterval(t1);
                setLine1Done(true);

                const p1 = setTimeout(() => {
                  let a = 0;
                  const t2 = setInterval(() => {
                    a++;
                    setLine2(LINE2.slice(0, a));
                    if (a >= LINE2.length) {
                      clearInterval(t2);
                      setLine2Done(true);

                      const p2 = setTimeout(() => {
                        let b = 0;
                        const t3 = setInterval(() => {
                          b++;
                          setLine3(LINE3.slice(0, b));
                          if (b >= LINE3.length) {
                            clearInterval(t3);

                            const p3 = setTimeout(() => {
                              setCtaVisible(true);
                              onCTAReady?.();
                            }, CTA_PAUSE);
                            timeouts.push(p3);
                          }
                        }, 40);
                        intervals.push(t3);
                      }, LINE_PAUSE);
                      timeouts.push(p2);
                    }
                  }, 35);
                  intervals.push(t2);
                }, LINE_PAUSE);
                timeouts.push(p1);
              }
            }, CHAR_SPEED);
            intervals.push(t1);
          }, SEG_PAUSE);
          timeouts.push(p0);
        }
      }, CHAR_SPEED);
      intervals.push(t0);

    }, 150);

    timeouts.push(startDelay);

    return () => {
      timeouts.forEach(clearTimeout);
      intervals.forEach(clearInterval);
    };
  }, [shouldStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCTAClick = () => {
    setIsVisible(false);
    scroller.scrollTo('profile-section-root', { smooth: true, duration: 600 });
  };

  return (
    <div className={`${styles.root} ${isVisible ? styles.visible : styles.hidden}`}>
      <div className={styles.line1}>
        {line1}
        {!line1Done && line1.length > 0 && <span className={styles.cursor}>▌</span>}
      </div>
      {line2.length > 0 && (
        <div className={`${styles.line2} ${line2Done ? styles.line2Dim : ''}`}>
          {line2}
          {!line2Done && <span className={styles.cursor}>▌</span>}
        </div>
      )}
      {line3.length > 0 && (
        <div className={styles.line3}>
          {line3}
          {line3.length < LINE3.length && <span className={styles.cursor}>▌</span>}
        </div>
      )}
      {ctaVisible && (
        <button className={styles.cta} onClick={handleCTAClick}>
          <span className={styles.prompt}>&gt;</span>
          <span className={styles.ctaText}> explore</span>
          <span className={styles.ctaCursor}>▌</span>
        </button>
      )}
    </div>
  );
};

export default HeroCTA_v2;
